import React, { Component, createRef } from "react";
import axios from "../../utils/axiosInstance";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "./ChatDashboard.css";

class ChatDashboard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      conversations: [],
      activeChatId: null,
      messages: [],
      host_id: "",
      inputText: "",
      isLoading: true,
      isSending: false,
      loadingMessages: false,
      isDemo: false,
      mobileView: "inbox",

      // Modal States
      showDetailsModal: false,
      showGuestDetailsModal: false,
    };

    this.chatEndRef = createRef();
  }

  componentDidMount() {
    this.getConversations();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.messages !== this.state.messages) {
      this.scrollToBottom();
    }
  }

  scrollToBottom = () => {
    setTimeout(() => {
      this.chatEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 50);
  };

  getConversations = async () => {
    try {
      const web_user_id = localStorage.getItem("web_user_id");
      const res = await axios.get(`/chat/conversations/${web_user_id}`);
      const conversations = res.data?.data || [];

      if (!conversations.length) throw new Error("Empty");

      this.setState(
        {
          conversations,
          activeChatId: conversations[0].id,
          host_id: conversations[0].host_id,
          isLoading: false,
          isDemo: false,
        },
        () => this.getMessages(conversations[0].id)
      );
    } catch (error) {
      this.setState(
        {
          conversations: this.demoConversations,
          activeChatId: this.demoConversations[0].id,
          host_id: this.demoConversations[0].host_id,
          isLoading: false,
          isDemo: true,
        },
        () => this.getMessages(this.demoConversations[0].id)
      );
    }
  };

  getMessages = async (chatId) => {
    this.setState({ loadingMessages: true });
    try {
      const web_user_id = localStorage.getItem("web_user_id");
      const res = await axios.get(`/chat/messages/${web_user_id}/${chatId}`);

      if (!res.data?.data?.length) throw new Error("Empty");

      this.setState({
        messages: res.data.data,
        loadingMessages: false,
      });
    } catch {
      this.setState({
        messages: this.demoMessages,
        loadingMessages: false,
      });
    }
  };

  sendMessage = async (e) => {
    e.preventDefault();
    const { host_id, inputText, messages, isDemo } = this.state;
    const messageText = inputText.trim();

    if (!messageText) return;

    const newMsg = {
      id: Date.now(),
      sender: "user",
      host_id,
      message: messageText,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };

    this.setState({
      messages: [...messages, newMsg],
      inputText: "",
      isSending: true,
    });

    if (isDemo) {
      setTimeout(() => {
        this.setState((prev) => ({
          messages: [
            ...prev.messages,
            {
              id: Date.now(),
              sender: "partner",
              message: "Thanks! We will review your request shortly.",
              time: "Just now",
            },
          ],
          isSending: false,
        }));
      }, 1000);
      return;
    }

    try {
      const web_user_id = localStorage.getItem("web_user_id");
      await axios.post("/chat/send", {
        host_id,
        customer_id: web_user_id,
        message: messageText,
      });
      this.setState({ isSending: false });
    } catch (error) {
      console.error(error);
      this.setState({ isSending: false });
    }
  };

  handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  };

  handleChatChange = (chatId) => {
    const selectedChat = this.state.conversations.find((chat) => chat.id === chatId);
    this.setState(
      {
        activeChatId: chatId,
        messages: [],
        host_id: selectedChat?.host_id || "",
        mobileView: "chat",
      },
      () => this.getMessages(chatId)
    );
  };

  handleMobileBack = () => {
    this.setState({ mobileView: "inbox" });
  };

  handlePayment = (id) => {
    if (!id) return;
    window.location.href = `/payment/${id}`;
  };

  parseGallery = (gallery) => {
    if (!gallery) return [];
    if (Array.isArray(gallery)) return gallery;
    try {
      const parsed = JSON.parse(gallery);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  getBaseUrl = () => {
    return axios.defaults.baseURL ? axios.defaults.baseURL.replace("/api", "") : "";
  };

  renderIcon = (type) => {
    const icons = {
      back: (
        <svg viewBox="0 0 24 24" aria-hidden="true" strokeWidth="2.5">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      ),
      send: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22 2 11 13" />
          <path d="m22 2-7 20-4-9-9-4Z" />
        </svg>
      ),
      call: (
        <svg viewBox="0 0 24 24" aria-hidden="true" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" stroke="currentColor">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      ),
    };
    return icons[type] || null;
  };

  getPricingDetails = (activeChat) => {
    const perDay = activeChat.per_day_amount || 0;
    const nights = activeChat.total_nights || 0;
    const discount = activeChat.discount || 0;
    const subTotal = perDay * nights;
    const totalPayable = subTotal - discount;
    const isPaid = activeChat.payment_status === "Paid";
    const paid = isPaid ? totalPayable : 0;
    const due = totalPayable - paid;

    return { perDay, nights, discount, subTotal, totalPayable, paid, due };
  };

  getButtonProps = (activeChat) => {
    const isConfirmed = activeChat.booking_status === "Confirmed";
    const isPaid = activeChat.payment_status === "Paid";
    const isExpired = activeChat.booking_status === "Expired" || activeChat.booking_status === "Cancelled";

    let btnText = "Continue to Payment";
    let btnAction = null;
    let btnDisabled = true;

    if (isPaid || isExpired) {
      btnText = "Book again";
      btnDisabled = false;
      btnAction = () => (window.location.href = "/searchresultmobile");
    } else if (isConfirmed) {
      btnText = "Confirm And Pay";
      btnDisabled = false;
      btnAction = () => this.handlePayment(activeChat.booking_id);
    } else {
      btnText = "Continue to Payment";
      btnDisabled = true;
    }

    return { btnText, btnAction, btnDisabled };
  };

  renderConversationList = () => {
    const { conversations, activeChatId, isLoading } = this.state;

    if (isLoading) {
      return (
        <div className="chat-loading-list">
          {[1, 2, 3, 4].map((item) => (
            <div className="chat-skeleton" key={item}>
              <div className="skeleton-avatar" />
              <div className="skeleton-content"><span /><span /><span /></div>
            </div>
          ))}
        </div>
      );
    }

    if (!conversations.length) {
      return (
        <div className="chat-empty">
          <div className="chat-empty-icon">💬</div>
          <h3>No messages yet</h3>
        </div>
      );
    }

    return conversations.map((chat) => (
      <button
        type="button"
        key={chat.id}
        className={`conversation-item ${activeChatId === chat.id ? "active" : ""}`}
        onClick={() => this.handleChatChange(chat.id)}
      >
        <div className="conversation-avatar">
          <img src={chat.avatar} alt={chat.host_name || "Host"} />
          <span className={`online-status ${chat.status === "online" ? "online" : "offline"}`} />
        </div>
        <div className="conversation-content">
          <div className="conversation-top">
            <h3>{chat.host_name || "Property Host"}</h3>
            <span className="conversation-date">{chat.date || ""}</span>
          </div>
          <p className="conversation-message">{chat.last_message || "No messages yet"}</p>
          <div className="conversation-bottom">
            <span className="conversation-booking">{chat.title || "Property booking"}</span>
            {chat.reviewNeeded && <span className="review-badge">Review</span>}
          </div>
        </div>
      </button>
    ));
  };

  renderChatHeader = (activeChat) => {
    return (
      <div className="chat-screen-header">
        <button
          type="button"
          className="mobile-back-button"
          onClick={this.handleMobileBack}
          aria-label="Back to inbox"
        >
          {this.renderIcon("back")}
        </button>

        <div className="active-user-avatar">
          <img src={activeChat.avatar} alt={activeChat.host_name || "Host"} />
          <span className="active-online-dot" />
        </div>

        <div className="active-user-info">
          <h2>{activeChat.host_name || "Property Host"}</h2>
          <span><i />Online</span>
        </div>

        <a
          href={`tel:${activeChat.phone || ""}`}
          className="chat-header-call"
          aria-label="Call Host"
        >
          {this.renderIcon("call")}
        </a>
      </div>
    );
  };

  renderMessages = (activeChat) => {
    const { messages, loadingMessages, isSending } = this.state;

    if (loadingMessages) {
      return (
        <div className="messages-loading">
          <div className="message-loader">Loading messages...</div>
        </div>
      );
    }

    return (
      <div className="messages-scroll-area">
        <div className="chat-date-divider"><span>Today</span></div>

        {activeChat.customer_name && (
          <div className="booking-request-alert">
            <div className="booking-alert-icon">✓</div>
            <div>
              <strong>{activeChat.customer_name}</strong> requested to book <strong>"{activeChat.title}"</strong>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`message-row ${msg.sender === "user" ? "sent" : "received"}`}>
            <div className="message-bubble">
              <div className="message-text" dangerouslySetInnerHTML={{ __html: msg.message }} />
              <div className="message-meta">
                <span>{msg.time}</span>
                {msg.sender === "user" && <span className="message-check">✓✓</span>}
              </div>
            </div>
          </div>
        ))}

        {isSending && (
          <div className="typing-indicator">
            <span /><span /><span />
          </div>
        )}
        <div ref={this.chatEndRef} />
      </div>
    );
  };

  renderChatInput = () => {
    const { inputText, isSending } = this.state;

    return (
      <div className="chat-input-area">
        <form className="chat-input-wrapper" onSubmit={this.sendMessage}>
          <button type="button" className="input-plus-button" aria-label="Add attachment">+</button>
          <textarea
            className="chat-input"
            placeholder="Write message... (Shift + Enter for new line)"
            value={inputText}
            rows={1}
            onChange={(e) => this.setState({ inputText: e.target.value })}
            onKeyDown={this.handleKeyDown}
          />
          <button
            type="submit"
            className="send-message-button"
            disabled={!inputText.trim() || isSending}
            aria-label="Send message"
          >
            {this.renderIcon("send")}
          </button>
        </form>
      </div>
    );
  };

  renderModals = (activeChat) => {
    const { showDetailsModal, showGuestDetailsModal } = this.state;
    const { perDay, nights, discount, subTotal, totalPayable, paid, due } = this.getPricingDetails(activeChat);
    const { btnText, btnAction, btnDisabled } = this.getButtonProps(activeChat);

    return (
      <>
        {showDetailsModal && (
          <div className="custom-modal-overlay" onClick={() => this.setState({ showDetailsModal: false })}>
            <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="custom-modal-header">
                <h3>Booking Details</h3>
                <button onClick={() => this.setState({ showDetailsModal: false })}>✕</button>
              </div>
              <div className="custom-modal-body details-scrollable">
                <div className="details-card">
                  <p className="details-title">{activeChat.title}</p>
                  
                  {/* Trip Details */}
                  <div className="details-section">
                    <h4>1. Booking Details & 2. Trip Details</h4>
                    <div className="details-row">
                      <span>Guests</span>
                      <strong>{activeChat.adults} Adults</strong>
                    </div>
                    <div className="details-row">
                      <span>Check In & Out</span>
                      <strong>{activeChat.date || "Not set"}</strong>
                    </div>
                    <div className="details-row">
                      <span>Status</span>
                      <strong>{activeChat.booking_status || "Pending"}</strong>
                    </div>
                  </div>

                  {/* Guest Details Trigger */}
                  <div className="details-section mt-15">
                    <h4>3. Guest Details</h4>
                    <button
                      className="modal-btn-secondary"
                      onClick={() => this.setState({ showDetailsModal: false, showGuestDetailsModal: true })}
                    >
                      Show Guest Details
                    </button>
                  </div>

                  {/* Contact Details */}
                  <div className="details-section mt-15">
                    <h4>4. Contact Details</h4>
                    <p className="text-muted" style={{ fontSize: '12px' }}>
                      Contact Number & Location will be provided once you Confirm & Pay
                    </p>
                  </div>

                  {/* Cancellation Policy */}
                  <div className="details-section mt-15">
                    <h4>5. Cancellation Policy</h4>
                    <p className="text-danger" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                      Non Refundable
                    </p>
                  </div>

                  {/* Price Details */}
                  <div className="details-section mt-15">
                    <h4>6. Price Details</h4>
                    <div className="details-row">
                      <span>BDT {perDay} x {nights} days</span>
                      <strong>{subTotal}</strong>
                    </div>
                    <div className="details-row">
                      <span>Discount</span>
                      <strong>{discount}</strong>
                    </div>
                  </div>

                  {/* Total Calculations */}
                  <div className="details-section mt-15" style={{ borderTop: '1px solid #edf1f5', paddingTop: '10px' }}>
                    <h4>7. Payment Summary</h4>
                    <div className="details-row">
                      <span>Total Payable</span>
                      <strong>{totalPayable}</strong>
                    </div>
                    <div className="details-row">
                      <span>Paid</span>
                      <strong>{paid}</strong>
                    </div>
                    <div className="details-row" style={{ color: '#004bb4' }}>
                      <span>Due</span>
                      <strong>{due}</strong>
                    </div>
                  </div>

                  {/* Action Button */}
                  <button
                    className="modal-btn-primary mt-15"
                    disabled={btnDisabled}
                    style={{ opacity: btnDisabled ? 0.6 : 1 }}
                    onClick={btnAction}
                  >
                    {btnText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showGuestDetailsModal && (
          <div className="custom-modal-overlay" onClick={() => this.setState({ showGuestDetailsModal: false })}>
            <div className="custom-modal-content small-modal" onClick={(e) => e.stopPropagation()}>
              <div className="custom-modal-header">
                <h3>Guest Details</h3>
                <button onClick={() => this.setState({ showGuestDetailsModal: false, showDetailsModal: true })}>✕</button>
              </div>
              <div className="custom-modal-body">
                <div className="guest-details-list">
                  <div className="details-row">
                    <span>Name</span>
                    <strong>{activeChat.customer_name || "N/A"}</strong>
                  </div>
                  <div className="details-row">
                    <span>Phone</span>
                    <strong>{activeChat.customer_phone || "N/A"}</strong>
                  </div>
                  <div className="details-row">
                    <span>Email</span>
                    <strong style={{ wordBreak: 'break-all' }}>{activeChat.customer_email || "N/A"}</strong>
                  </div>
                </div>
                <button
                  className="modal-btn-secondary mt-15"
                  onClick={() => this.setState({ showGuestDetailsModal: false, showDetailsModal: true })}
                >
                  Back to Details
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  renderPropertyDetails = (activeChat) => {
    const baseUrl = this.getBaseUrl();
    const gallery = this.parseGallery(activeChat.gallery_images);
    const images = [];

    if (activeChat.feature_image) {
      images.push(
        activeChat.feature_image.startsWith("http")
          ? activeChat.feature_image
          : `${baseUrl}/uploads/posts/${activeChat.feature_image}`
      );
    }
    gallery.forEach((image) => {
      if (image && !images.includes(image)) images.push(`${baseUrl}/uploads/posts/${image}`);
    });

    const { perDay, nights, discount, subTotal, totalPayable, paid, due } = this.getPricingDetails(activeChat);
    const { btnText, btnAction, btnDisabled } = this.getButtonProps(activeChat);

    return (
      <aside className="property-panel">
        <div className="property-panel-header" style={{ justifyContent: 'space-between' }}>
          <div>
            <span className="panel-eyebrow">BOOKING DETAILS</span>
            <h3>Property</h3>
          </div>
          <button 
            className="prop-details-btn-desktop"
            onClick={() => this.setState({ showDetailsModal: true })}
          >
            Details Popup
          </button>
        </div>

        <div className="property-scroll">
          {images.length > 0 ? (
            <div className="property-gallery">
              <Swiper navigation modules={[Navigation]} spaceBetween={0} slidesPerView={1}>
                {images.map((imgSrc, index) => (
                  <SwiperSlide key={`${imgSrc}-${index}`}>
                    <img src={imgSrc} alt={`Property ${index + 1}`} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <div className="property-image-placeholder">
              <span>🏠</span>
              <p>Property image</p>
            </div>
          )}

          <div className="property-info-card">
            <h3>{activeChat.title || "Property Booking"}</h3>
            <div className="property-status-tag">
               Status: <strong>{activeChat.booking_status || "Pending"}</strong>
            </div>

            {/* Trip Details */}
            <div className="desktop-details-section">
              <h4>Trip Details</h4>
              <p><strong>Dates:</strong> {activeChat.date || "Not selected"}</p>
              <p><strong>Guests:</strong> {activeChat.adults || 0} Adults</p>
            </div>

            {/* Guest Details */}
            <div className="desktop-details-section">
              <button 
                className="desktop-inline-btn"
                onClick={() => this.setState({ showGuestDetailsModal: true })}
              >
                View Guest Details
              </button>
            </div>

            {/* Contact Details */}
            <div className="desktop-details-section">
              <h4>Contact Details</h4>
              <p className="text-muted">Contact Number & Location will be provided once you Confirm & Pay</p>
            </div>

            {/* Cancellation Policy */}
            <div className="desktop-details-section">
              <h4>Cancellation Policy</h4>
              <p className="text-danger">Non Refundable</p>
            </div>

            {/* Price Details */}
            <div className="price-summary">
              <h4>Price Details</h4>
              <div className="price-row">
                <span>BDT {perDay} × {nights} days</span>
                <strong>{subTotal}</strong>
              </div>
              <div className="price-row">
                <span>Discount</span>
                <strong>{discount}</strong>
              </div>
              
              <div className="price-row total">
                <span>Total Payable</span>
                <strong>{totalPayable}</strong>
              </div>
              <div className="price-row">
                <span>Paid</span>
                <strong>{paid}</strong>
              </div>
              <div className="price-row due">
                <span>Due</span>
                <strong>{due}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="property-action-footer">
          <button
            type="button"
            className="property-payment-button"
            disabled={btnDisabled}
            style={{ opacity: btnDisabled ? 0.6 : 1 }}
            onClick={btnAction}
          >
            {btnText}
          </button>
        </div>
      </aside>
    );
  };

  render() {
    const { conversations, activeChatId, mobileView } = this.state;
    const activeChat = conversations.find((chat) => chat.id === activeChatId) || {};
    const baseUrl = this.getBaseUrl();
    const isConfirmed = activeChat.booking_status === "Confirmed";
    
    const featureImgSrc = activeChat.feature_image?.startsWith("http") 
      ? activeChat.feature_image 
      : (activeChat.feature_image ? `${baseUrl}/uploads/posts/${activeChat.feature_image}` : null);

    const { btnText, btnAction, btnDisabled } = this.getButtonProps(activeChat);

    return (
      <div className="chat-dashboard">
        {this.renderModals(activeChat)}

        {/* DESKTOP */}
        <div className="chat-desktop-layout">
          <aside className="inbox-sidebar">
            <div className="inbox-header">
              <div>
                <span className="page-eyebrow">MESSAGES</span>
                <h1>Inbox</h1>
              </div>
              <div className="inbox-count">{conversations.length}</div>
            </div>
            <div className="conversation-list">{this.renderConversationList()}</div>
          </aside>

          <main className="chat-main-panel">
            {this.renderChatHeader(activeChat)}
            <div className="messages-area">{this.renderMessages(activeChat)}</div>
            {this.renderChatInput()}
          </main>

          {this.renderPropertyDetails(activeChat)}
        </div>

        {/* MOBILE */}
        <div className="chat-mobile-layout">
          {mobileView === "inbox" ? (
            <div className="mobile-inbox-screen">
              <div className="mobile-inbox-header">
                <div>
                  <span className="page-eyebrow">MESSAGES</span>
                  <h1>Inbox</h1>
                </div>
                {conversations.length > 0 && <div className="mobile-inbox-count">{conversations.length}</div>}
              </div>
              <div className="mobile-conversation-list">{this.renderConversationList()}</div>
            </div>
          ) : (
            <div className="mobile-chat-screen">
              {this.renderChatHeader(activeChat)}

              <div className="mobile-chat-property">
                {featureImgSrc ? (
                   <img src={featureImgSrc} alt="Property" className="mobile-prop-thumb" />
                ) : (
                   <div className="mobile-prop-thumb-placeholder">🏠</div>
                )}
                
                <div className="mobile-prop-details">
                  <div className="mobile-prop-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ flex: 1, paddingRight: '10px' }}>{activeChat.title || "Property Booking"}</h4>
                    <button 
                      className="prop-details-btn" 
                      onClick={() => this.setState({ showDetailsModal: true })}
                    >
                      Details
                    </button>
                  </div>
                  <div className="mobile-prop-meta">
                    <span className={`status-badge ${isConfirmed ? 'confirmed' : ''}`}>
                       {activeChat.booking_status || 'Pending'}
                    </span>
                    <span>📅 {activeChat.date}</span>
                    <span>👥 {activeChat.adults} Guests</span>
                  </div>
                </div>
              </div>

              <div className="mobile-messages-area">{this.renderMessages(activeChat)}</div>
              {this.renderChatInput()}

              <div className="mobile-payment-bar">
                <button 
                  type="button" 
                  disabled={btnDisabled}
                  style={{ opacity: btnDisabled ? 0.6 : 1 }}
                  onClick={btnAction}
                >
                  {btnText}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ChatDashboard;

